/**
 * The Kontroll workspace's frame (manager redesign 2026-10-09, V7.5.6 #275): one header over Gjennomgang
 * (/timeliste), Registreringer (/timeliste?fane=registreringer, every record in the same list) and Per ansatt
 * (/timeliste?fane=ansatte), so the three read as one place with one working list at a time. «Last ned» holds the files each page writes from what it has
 * loaded; the Personalliste and the §10-7 print moved to Rapporter (#173, owner 2026-10-19: Rapporter holds
 * what leaves the app). «Lønnsgrunnlag» is the handoff to Rapporter for whoever may read pay.
 */
import { useCan } from '@/auth/caps';
import { T } from '@/i18n';
import { Menu } from '@/ui';
import { Icon } from '@/ui/icons';
import { PageHead, Tabs } from '@/ui/page';
import { Link, useNavigate } from 'react-router-dom';
import { worktimeViews } from '../lonn/Head';
import './timer.css';

export interface ReportItem {
  key: string;
  label: string;
  onSelect: () => void;
  disabled?: boolean;
}

export type TimerTab = 'gjennomgang' | 'registreringer' | 'ansatte';

export function TimerHead({
  tab,
  scope = '',
  reports = [],
  onAdd,
  addDisabled,
  payroll,
}: {
  /** shown but off: a reader without timeliste.rette, or data that must be read again first (D167) */
  addDisabled?: boolean;
  tab: TimerTab;
  /** the period, team, person and search to carry between Gjennomgang and Per ansatt, as a query string */
  scope?: string;
  /** the files this page can write now */
  reports?: ReportItem[];
  /** the manual registration; shown only with timeliste.rette */
  onAdd?: () => void;
  /** the month the Lønnsgrunnlag link opens, YYYY-MM */
  payroll?: string;
}) {
  const can = useCan();
  const go = useNavigate();
  // without Rapporter (pay.view) the Personalliste and the §10-7 print stay reachable here
  const items = can('timerloenn.se') ? reports : [...reports, ...worktimeViews(go)];
  const q = scope ? `?${scope}` : '';
  return (
    <PageHead
      icon="clipboard"
      title={T('Kontroll')}
      sub={T('Avklar faktisk arbeidstid. Godkjente timer går videre til rapporter.')}
      actions={
        <>
          {onAdd && (addDisabled !== undefined || can('timeliste.rette')) && (
            <button
              type="button"
              className="btn kadd"
              onClick={onAdd}
              disabled={!!addDisabled || !can('timeliste.rette')}
            >
              <Icon name="plus" />
              {T('Legg til registrering')}
            </button>
          )}
          <Menu
            items={items}
            trigger={
              <button
                type="button"
                className="btn kdl"
                data-testid="timer-reports"
                aria-label={T('Last ned')}
              >
                <Icon name="download" />
                <span className="kdlt">{T('Last ned')}</span>
              </button>
            }
          />
          {payroll && can('timerloenn.se') && (
            // the page's way forward is its primary action (#154 pin kontroll-1440)
            <Link className="btn pri" to={`/lonn?mnd=${payroll}`}>
              {T('Lønnsgrunnlag')}
              <Icon name="arrowRight" />
            </Link>
          )}
        </>
      }
    >
      <Tabs
        label={T('Kontroll')}
        value={tab}
        items={[
          { key: 'gjennomgang', label: T('Gjennomgang'), icon: 'clipboard', to: `/timeliste${q}` },
          {
            key: 'registreringer',
            label: T('Registreringer'),
            icon: 'clock',
            to: `/timeliste?fane=registreringer${scope ? `&${scope}` : ''}`,
          },
          {
            key: 'ansatte',
            label: T('Per ansatt'),
            icon: 'users',
            to: `/timeliste?fane=ansatte${scope ? `&${scope}` : ''}`,
          },
        ]}
      />
    </PageHead>
  );
}
